import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Form, Input, Select } from 'antd'
import type { OrderGroup } from '@super-mega/server/contracts/order'
import { useDebouncedValue } from '@/hooks/use-debounced-value.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { mergeNumberOptions } from '@/utils/select-options.ts'

type OrderGroupFormValues = {
  customerId?: number
  name?: string
}

type OrderGroupEditorModalProps = {
  editing?: OrderGroup | 'new'
  onClose: () => void
  onSaved: () => Promise<void>
}

export function OrderGroupEditorModal(props: OrderGroupEditorModalProps) {
  const key = props.editing === 'new' ? 'new' : (props.editing?.id ?? 'closed')
  return <OrderGroupEditorSession key={key} {...props} />
}

function OrderGroupEditorSession({ editing, onClose, onSaved }: OrderGroupEditorModalProps) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<OrderGroupFormValues>()
  const [customerSearch, setCustomerSearch] = useState('')
  const customerKeyword = useDebouncedValue(customerSearch.trim() || undefined)
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({ purpose: 'order.group.read', keyword: customerKeyword }),
    enabled: Boolean(editing),
    staleTime: 60_000
  })
  const createGroup = useMutation(trpc.order.groups.create.mutationOptions())
  const updateGroup = useMutation(trpc.order.groups.update.mutationOptions())
  const isCreating = editing === 'new'
  const isSaving = createGroup.isPending || updateGroup.isPending
  const initialValues: Partial<OrderGroupFormValues> =
    editing && editing !== 'new' ? { customerId: editing.customerId, name: editing.name } : { name: '' }
  const customerOptions = mergeNumberOptions(
    editing && editing !== 'new' ? { label: editing.customerName, value: editing.customerId } : undefined,
    customers.data ?? []
  )

  const save = async (values: OrderGroupFormValues) => {
    if (!values.customerId) {
      return false
    }
    try {
      if (isCreating) {
        await createGroup.mutateAsync({ customerId: values.customerId, name: values.name?.trim() || undefined })
      } else if (editing) {
        await updateGroup.mutateAsync({ id: editing.id, name: values.name?.trim() ?? '' })
      }
      void message.success(isCreating ? '订单组已创建' : '订单组已更新')
      await onSaved()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, isCreating ? '订单组创建失败' : '订单组更新失败'))
      return false
    }
  }

  return (
    <ModalForm<OrderGroupFormValues>
      form={form}
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
      onFinish={save}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      open={Boolean(editing)}
      scrollToFirstError
      submitter={{
        resetButtonProps: { onClick: onClose },
        searchConfig: { resetText: '取消', submitText: '保存订单组' },
        submitButtonProps: { loading: isSaving }
      }}
      title={isCreating ? '新建订单组' : `编辑订单组 · ${editing?.name ?? ''}`}
      width="34rem"
    >
      <Form.Item
        extra={isCreating ? '一个订单组只能归属于一个客户，组内可放置多个订单。' : '订单组客户创建后不可修改。'}
        label="客户"
        name="customerId"
        rules={[{ message: '请选择客户', required: true }]}
      >
        <Select
          disabled={!isCreating}
          loading={customers.isLoading || customers.isFetching}
          options={customerOptions}
          placeholder="请选择客户"
          showSearch={{ filterOption: false, onSearch: setCustomerSearch, optionFilterProp: 'label' }}
        />
      </Form.Item>
      <Form.Item
        label={isCreating ? '订单组名称（可选）' : '订单组名称'}
        name="name"
        rules={isCreating ? [{ max: 255 }] : [{ max: 255, message: '请输入订单组名称', required: true }]}
      >
        <Input maxLength={255} placeholder="可填写名称，留空自动生成订单组号" />
      </Form.Item>
    </ModalForm>
  )
}
